import { AionCta } from "@/components/site/AionCta";
import { OG_ABOUT } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Heart } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Raj Shamani Wife & Personal Life — What's Confirmed";
const DESC =
  "Is Raj Shamani married? What is publicly confirmed about his wife, relationship status, family, home city and personal life — with unsourced claims clearly flagged.";
const URL = "https://rajshamani.life/raj-shamani-wife";
const OG = OG_ABOUT;

const faq = [
  {
    q: "Is Raj Shamani married?",
    a: "There is no public announcement of a marriage. Raj Shamani has never confirmed a wife or spouse in any interview, podcast episode or on his own social media, and no verified record exists. Pages that state a wife's name are repeating unsourced claims.",
  },
  {
    q: "Who is Raj Shamani's wife?",
    a: "No wife has been publicly confirmed. Raj Shamani keeps his relationships private and speaks publicly about business, the Figuring Out podcast and investing rather than his personal life.",
  },
  {
    q: "Does Raj Shamani have a girlfriend?",
    a: "Raj Shamani has not publicly disclosed a relationship. Any name circulating online is speculation and is not confirmed by him or his team.",
  },
  {
    q: "Why is there so little information about Raj Shamani's personal life?",
    a: "It is a deliberate choice. Raj Shamani has said in multiple interviews that he keeps family and relationships out of public content, which is also why details about his father, siblings and home are not published.",
  },
  {
    q: "What is publicly known about Raj Shamani's personal life?",
    a: "He was born and raised in Indore, Madhya Pradesh, in a Marwari business family, studied at Daly College, joined the family manufacturing business Shamani Industries at 16, and now splits his time between Indore and Mumbai.",
  },
  {
    q: "How old is Raj Shamani?",
    a: "Raj Shamani was born in 1996 and is in his late twenties. The date of birth, current age and birthday details are on the dedicated Raj Shamani age page.",
  },
];

export const Route = createFileRoute("/raj-shamani-wife")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani personal life", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          breadcrumb: { "@id": `${URL}#breadcrumb` },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            url: "https://rajshamani.life/about",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: WifePage,
});

function WifePage() {
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Wife" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Heart size={14} /> Personal life
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Raj Shamani <span className="text-gradient-gold">wife</span> &amp; personal life
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            <strong className="text-foreground">
              Raj Shamani has not publicly confirmed a wife or marriage.
            </strong>{" "}
            No spouse, engagement or relationship has been announced by him or his team, and every
            name circulating online is unsourced. This page collects what is actually verified about
            his personal life — and clearly marks what is not.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Personal life at a glance</h2>
        <dl className="rounded-xl border border-border bg-card divide-y divide-border/60">
          {[
            ["Marital status", "Not publicly disclosed"],
            ["Wife / spouse", "None confirmed"],
            ["Relationship status", "Not disclosed"],
            ["Born", "1996, Indore, Madhya Pradesh"],
            ["Family", "Marwari business family; father runs Shamani Industries"],
            ["Based in", "Indore and Mumbai, India"],
            ["Public focus", "Business, investing and the Figuring Out podcast"],
          ].map(([k, v]) => (
            <div key={k} className="px-6 py-4 flex flex-wrap gap-2 justify-between">
              <dt className="text-sm uppercase tracking-wide text-gold">{k}</dt>
              <dd className="text-muted-foreground sm:text-right">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">Why so little is public</h2>
        <p className="text-muted-foreground max-w-3xl leading-relaxed mb-4">
          Raj Shamani has been consistent about keeping his private life out of his content. His
          public output — hundreds of podcast episodes, a book and daily social posts — is almost
          entirely about business, money and building. Family members, relationships and his home
          are not part of that, by design.
        </p>
        <p className="text-muted-foreground max-w-3xl leading-relaxed">
          That is also why aggregator sites fill the gap with invented details. Where something has
          not been confirmed by Raj himself, this site records it as not disclosed rather than
          guessing. The verified family details that do exist are on the{" "}
          <Link to="/raj-shamani-family" className="text-gold underline underline-offset-4">
            family and education page
          </Link>
          .
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-muted-foreground">
          See also{" "}
          <Link to="/raj-shamani-age" className="text-gold underline underline-offset-4">
            his age and date of birth
          </Link>{" "}
          and the full{" "}
          <Link to="/raj-shamani-biography" className="text-gold underline underline-offset-4">
            biography
          </Link>
          .
        </p>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/raj-shamani-wife" />
      <RelatedPages pages={["aion", "about", "faq", "ventures", "book", "podcast"]} />
    </div>
  );
}
